import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "@/components/ui/input-otp";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const TwoFactorSettings = ({ user, updateUser }) => {
    const [loading, setLoading] = useState(true);
    const [enabled, setEnabled] = useState(false);
    const [showEnableDialog, setShowEnableDialog] = useState(false);
    const [showDisableDialog, setShowDisableDialog] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [waitingForOTP, setWaitingForOTP] = useState(false);

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            const response = await authAPI.get2FAStatus();
            setEnabled(response.data.enabled);
        } catch (err) {
            console.error('Failed to fetch 2FA status:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEnableClick = async () => {
        setProcessing(true);
        try {
            await authAPI.enable2FA();
            setWaitingForOTP(true);
            setShowEnableDialog(true);
            toast.info('Cod de verificare trimis pe email');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la activarea 2FA');
        } finally {
            setProcessing(false);
        }
    };

    const handleConfirmEnable = async () => {
        if (otpCode.length !== 6) {
            toast.error('Introdu codul complet de 6 cifre');
            return;
        }

        setProcessing(true);
        try {
            await authAPI.confirmEnable2FA({ otp_code: otpCode });
            setEnabled(true);
            setShowEnableDialog(false);
            setOtpCode('');
            setWaitingForOTP(false);
            updateUser?.({ two_factor_enabled: true });
            toast.success('Autentificarea în doi pași a fost activată!');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Cod incorect');
            setOtpCode('');
        } finally {
            setProcessing(false);
        }
    };

    const handleDisableClick = () => {
        setShowDisableDialog(true);
    };

    const handleConfirmDisable = async () => {
        setProcessing(true);
        try {
            await authAPI.disable2FA({ password });
            setEnabled(false);
            setShowDisableDialog(false);
            setPassword('');
            updateUser?.({ two_factor_enabled: false });
            toast.success('Autentificarea în doi pași a fost dezactivată');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la dezactivare');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <Card className="rounded-2xl">
                <CardContent className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className="rounded-2xl" data-testid="2fa-settings-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        {enabled ? (
                            <ShieldCheck className="w-5 h-5 text-green-500" />
                        ) : (
                            <ShieldOff className="w-5 h-5 text-muted-foreground" />
                        )}
                        Autentificare în doi pași (2FA)
                    </CardTitle>
                    <CardDescription>
                        {enabled 
                            ? 'Contul tău este protejat cu verificare prin email'
                            : 'Adaugă un nivel extra de securitate pentru contul tău'
                        }
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${enabled ? 'bg-green-100' : 'bg-muted'}`}>
                                <Mail className={`w-5 h-5 ${enabled ? 'text-green-600' : 'text-muted-foreground'}`} />
                            </div>
                            <div>
                                <p className="font-medium">Verificare prin Email</p>
                                <p className="text-sm text-muted-foreground">
                                    {enabled 
                                        ? 'Vei primi un cod la fiecare autentificare' 
                                        : 'Primește un cod de 6 cifre pe email'
                                    }
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={enabled}
                            onCheckedChange={() => enabled ? handleDisableClick() : handleEnableClick()}
                            disabled={processing}
                            data-testid="2fa-toggle"
                        />
                    </div>

                    {enabled && (
                        <div className="mt-4 p-3 bg-green-50 rounded-xl border border-green-100">
                            <p className="text-sm text-green-700 flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4" />
                                2FA este activ. La fiecare autentificare vei primi un cod pe {user?.email}
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Enable 2FA Dialog */}
            <Dialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-primary" />
                            Activare 2FA
                        </DialogTitle>
                        <DialogDescription>
                            Introdu codul de 6 cifre trimis la {user?.email}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        <div className="flex justify-center">
                            <InputOTP
                                maxLength={6}
                                value={otpCode}
                                onChange={(value) => setOtpCode(value)}
                                data-testid="enable-2fa-otp"
                            >
                                <InputOTPGroup>
                                    <InputOTPSlot index={0} className="w-11 h-12 text-lg" />
                                    <InputOTPSlot index={1} className="w-11 h-12 text-lg" />
                                    <InputOTPSlot index={2} className="w-11 h-12 text-lg" />
                                    <InputOTPSlot index={3} className="w-11 h-12 text-lg" />
                                    <InputOTPSlot index={4} className="w-11 h-12 text-lg" />
                                    <InputOTPSlot index={5} className="w-11 h-12 text-lg" />
                                </InputOTPGroup>
                            </InputOTP>
                        </div>

                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1 rounded-full"
                                onClick={() => {
                                    setShowEnableDialog(false);
                                    setOtpCode('');
                                    setWaitingForOTP(false);
                                }}
                            >
                                Anulează
                            </Button>
                            <Button
                                className="flex-1 rounded-full"
                                onClick={handleConfirmEnable}
                                disabled={processing || otpCode.length !== 6}
                                data-testid="confirm-enable-2fa"
                            >
                                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Activează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Disable 2FA Dialog */}
            <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <ShieldOff className="w-5 h-5" />
                            Dezactivare 2FA
                        </DialogTitle>
                        <DialogDescription>
                            Confirmă cu parola ta pentru a dezactiva autentificarea în doi pași
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="disable-password">Parola curentă</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="disable-password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 pr-10 rounded-xl"
                                    data-testid="disable-2fa-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                            <p className="text-sm text-amber-700">
                                ⚠️ Dezactivarea 2FA va reduce securitatea contului tău.
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1 rounded-full"
                                onClick={() => {
                                    setShowDisableDialog(false);
                                    setPassword('');
                                }}
                            >
                                Anulează
                            </Button>
                            <Button
                                variant="destructive"
                                className="flex-1 rounded-full"
                                onClick={handleConfirmDisable}
                                disabled={processing || !password}
                                data-testid="confirm-disable-2fa"
                            >
                                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Dezactivează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TwoFactorSettings;
